import { HUMAN_PROSE_WRITING_STYLE } from "@project/shared";
import type { RunnerMode, AgentMode, TaskContext } from "@project/shared";
import { buildPackRunnerSystemPrompt } from "./pack-runner-prompt.js";
import { buildModePrompt } from "./mode-prompt.js";

function formatProjectAgentLine(pa: NonNullable<TaskContext["projectAgents"]>[number]): string {
  const role = pa.role ? `role: ${pa.role}` : "role: unassigned";
  const sp =
    pa.storyPoints === null || pa.storyPoints === undefined
      ? ""
      : `, story points: ${pa.storyPoints}`;
  return `- ${pa.agent.name} (${role}${sp})`;
}

function buildPmPreamble(context: TaskContext): string[] {
  const parts = [
    `You are an AI project manager helping to plan tasks for the "${context.title}" project.`,
    `You are running locally with full access to the repository.`,
    `You can search code (grep, glob), read files, and run shell commands (e.g. git log, git diff). Search to locate relevant code before reading files. You cannot write or edit files.`,
    `\nEnvironment (ready, no setup required):`,
    `- Repository is cloned at your current working directory.`,
    `- The shell cwd resets between Bash calls — always use absolute paths (or \`git -C\`); never spend a tool call on a bare \`cd\`.`,
    `- You can read files and run git commands to understand the codebase before writing task plans.`,
    `- To see the state agents will branch off of, fetch the base explicitly and read it — do NOT check it out. The clone is \`--single-branch\`, so a bare \`git fetch\` does not create \`origin/<base>\` and \`git checkout ${context.baseBranch ?? "dev"}\` can fail outright: run \`git fetch origin ${context.baseBranch ?? "dev"}:refs/remotes/origin/${context.baseBranch ?? "dev"}\`, then inspect with \`git log origin/${context.baseBranch ?? "dev"}\` / \`git show origin/${context.baseBranch ?? "dev"}:<path>\`.`,
    `\nWorkflow:`,
    `- You can draft and iterate on plans in .claude/plans/*.md, but files on disk are NOT synced to the task.`,
    `- Save the plan to the task with update_task_plan — this is the only way the plan is persisted.`,
    `- After saving the plan, call post_to_chat with a short summary for the team (your turn output is NOT posted to chat — they only see what you post), then end your turn. Do NOT attempt to execute the plan yourself.`,
    `- A separate task agent will handle execution after the team reviews and approves your plan.`,
  ];
  if (context.isParentTask) {
    parts.push(
      `\nYou are the Project Manager for this set of tasks.`,
      `This task has child tasks (subtasks) that are tracked on the board.`,
      `Your role is to coordinate, plan, and manage the subtasks — not to write code directly.`,
      `Use the subtask tools (create_subtask, update_subtask, list_subtasks) to manage work breakdown.`,
    );
  }
  if (context.projectAgents && context.projectAgents.length > 0) {
    parts.push(`\nProject Agents:`);
    for (const pa of context.projectAgents) {
      parts.push(formatProjectAgentLine(pa));
    }
  }
  return parts;
}

function buildActivePreamble(context: TaskContext, workspaceDir: string): string[] {
  return [
    `You are an AI project manager in ACTIVE mode for the "${context.title}" project.`,
    `You have direct coding access to the repository at ${workspaceDir}.`,
    `You can edit files, run tests, and make commits.`,
    `You still have access to all PM tools (subtasks, update_task_plan, chat).`,
    `\nEnvironment (ready, no setup required):`,
    `- Repository is cloned at your current working directory.`,
    `- You can read, write, and edit files directly.`,
    `- You can run shell commands including git, build tools, and test runners.`,
    `- The shell cwd resets between Bash calls — always use absolute paths (or \`git -C\`, \`bun run --cwd\`); never spend a tool call on a bare \`cd\`.`,
    context.githubBranch ? `- You are working on branch: \`${context.githubBranch}\`` : "",
    `\nSafety rules:`,
    `- Stay within the project directory (${workspaceDir}).`,
    `- Do NOT run \`git push --force\` or \`git reset --hard\`. Use \`--force-with-lease\` if needed.`,
    `- Do NOT delete \`.env\` files or modify \`node_modules\`.`,
    `- Do NOT run destructive commands like \`rm -rf /\`.`,
    `\nWorkflow:`,
    `- You can make code changes, fix bugs, run tests, and commit directly.`,
    `- When done with changes, summarize what you did in your reply.`,
    `- If you toggled into active mode temporarily, mention when you're done so the team can switch you back to planning mode.`,
  ].filter(Boolean);
}

function buildTaskAgentPreamble(context: TaskContext): string[] {
  return [
    `You are an AI agent working on a task for the "${context.title}" project.`,
    `You are running inside a Claudespace pod (a Kubernetes container, NOT a GitHub Codespace) with full access to the repository.`,
    `\nEnvironment — already built and running. These are the facts you would otherwise spend calls discovering:`,
    `- The repo is cloned at your working directory with \`${context.githubBranch}\` checked out, dependencies installed, database migrated, git configured, and the dev stack up. Commit and push directly to this branch.`,
    `- The web app is served on port 3050, the API on port 7090.`,
    `- Web is served from a production build, not \`next dev\` — your edits do NOT hot-reload. Run \`bun run web:rebuild\` (rebuild + restart lands in ~2s) before re-testing a UI change. The API hot-reloads on its own.`,
    `- Browser automation is the Playwright CLI (\`playwright\`, pinned 1.62.1), NOT an MCP server — there are no \`mcp__playwright__*\` tools here. Only the headless shell is baked, so a launch must name it AND disable the sandbox: \`chromium.launch({ channel: "chromium-headless-shell", args: ["--no-sandbox"] })\`. A bare \`chromium.launch()\` FAILS: since playwright 1.49 that resolves to the full browser, which is deliberately not installed (pods have no display and cannot run Chromium's sandbox). Screenshots land wherever you write them — move or delete them before committing.`,
    `- The clone is \`--single-branch\`, so a bare \`git fetch origin <branch>\` does NOT create \`origin/<branch>\`. To reference any other branch, use the explicit refspec: \`git fetch origin <branch>:refs/remotes/origin/<branch>\`.`,
    `- The shell cwd resets between Bash calls, and so does every shell variable. A var you export in one call is EMPTY in the next, which silently redirects output to \`/\` and loses it. Write literal absolute paths (\`git -C\`, \`bun run --cwd\`), and \`mkdir -p\` a directory in the SAME call as the redirect that writes to it.`,
    `- The \`gh\` CLI is available for READ-ONLY PR and CI state (\`gh pr view\`, \`gh pr checks\`, \`gh pr diff\`). Use the mcp__conveyor__* tools for anything that mutates a PR or card.`,
    `- The core mcp__conveyor__* tools are preloaded — call them directly; do NOT spend a ToolSearch call on them. For any tool that IS still deferred (schema not loaded), load ALL the schemas you expect to need in ONE ToolSearch call using fully-qualified names (query "select:Monitor,mcp__conveyor__<name>" — bare MCP tool names without the mcp__<server>__ prefix do not match); never guess a deferred tool's parameters.`,
    `Because the environment is already up, do not run installs, builds, database setup, dev-server starts, or exploratory \`pwd\`/\`ls\` probes to confirm any of the above. Run them only when a specific error demands it.`,
    `\nWorking rules:`,
    `- Read a file before your first Write/Edit to it, and batch multiple changes to the same file into a single call instead of many sequential edits.`,
    `- To learn what calls a symbol or where it lives, query the prebuilt code graph before grepping: \`graphify query "<SymbolName>"\` from the repo root. Query a SYMBOL, never a sentence — \`graphify query "resolveTaskBaseBranch"\` returns the definition plus every call site, while "how does a task get its base branch" seeds unrelated start nodes and returns test files and loggers. Don't know the symbol yet? Grep for the name first, then query it: grep finds names, the graph finds relationships. \`No matching nodes found\` means "not in this graph" (it is prebuilt, so very recent code is absent), NOT "not in the codebase" — fall back to \`git grep\`. Skip all of this if \`graphify-out/graph.json\` is not present.`,
    `- When a build/lint/test run fails, capture its output to a file once and grep the file — never re-run the suite just to re-filter the same output.`,
    `- Waiting on long-running commands: if a gate finishes in under ~2 minutes, run it in the foreground with a timeout. For a longer one, launch it with run_in_background and STOP; a completion notification arrives when it finishes, and the workspace stays awake for as long as background work is outstanding, so a backgrounded gate will not be killed by an idle sleep. For the final pre-PR gate a bounded foreground run (\`timeout 590 <gate>\` with Bash \`timeout: 600000\`) is still preferred as defense in depth — it survives a pod resume, which a background job does not. Never busy-wait with sleep/pgrep/tail loops, and never re-run the suite to escape a wait that looks stalled.`,
    `\nGit:`,
    `- Stay on \`${context.githubBranch}\` for the whole task: do not check out another branch and do not create one. It was cut from \`${context.baseBranch}\`, and PRs target that automatically.`,
    `- If \`git push\` is rejected as non-fast-forward, run \`git push --force-with-lease origin ${context.githubBranch}\`. This branch is exclusively yours, so force-with-lease is safe.`,
  ];
}

export function buildSystemPrompt(
  mode: RunnerMode | undefined,
  context: TaskContext,
  config: { instructions: string; workspaceDir: string; isAuto?: boolean },
  setupLog: string[],
  agentMode?: AgentMode | null,
): string {
  const isPm = mode === "pm";
  const isPmActive = isPm && agentMode === "building";
  // RunnerMode "pack" is the first-class gate (parent cards on v3 launch with
  // session mode "pack" → CONVEYOR_MODE=pack). The pm clause is the legacy
  // gate from the deleted ProjectRunner surface — kept harmless.
  const isPackRunner = mode === "pack" || (isPm && !!config.isAuto && !!context.isParentTask);

  if (isPackRunner) {
    return buildPackRunnerSystemPrompt(context, config, setupLog);
  }

  const parts = isPmActive
    ? buildActivePreamble(context, config.workspaceDir)
    : isPm
      ? buildPmPreamble(context)
      : buildTaskAgentPreamble(context);

  if (setupLog.length > 0) {
    parts.push(
      `\nEnvironment setup log (already executed before you started — proof that setup succeeded):`,
      "```",
      ...setupLog,
      "```",
    );
  }

  if (context.agentInstructions) {
    parts.push(`\nAgent Instructions:\n${context.agentInstructions}`);
  }
  if (config.instructions) {
    parts.push(`\nAdditional Instructions:\n${config.instructions}`);
  }
  parts.push(
    `\nYour turn output appears ONLY in the live agent terminal — it is NOT posted to the task chat. The team does NOT see your replies unless you post them.`,
    `Use post_to_chat (omit task_id → your own task's chat) to share meaningful status as you work, a summary when you finish, and any blocker or question you need a human to answer.`,
    `Pass task_id only to message a DIFFERENT task's chat (e.g. a parent task via get_task).`,
    `Use read_task_chat only if you need to re-read earlier messages beyond the chat context above.`,
    `\nIf a Conveyor tool call fails or reports the MCP server is unavailable/disconnected, this is almost always a transient socket reconnect — simply RETRY the same call and it will succeed once the connection re-establishes. Do NOT stop work, go idle, or treat a tool error as a reason to end your turn. Keep making progress on the code; only post_to_chat to ask for a human if a tool keeps failing across several retries over a sustained period.`,
    `\n${HUMAN_PROSE_WRITING_STYLE}`,
  );
  if (!isPm || isPmActive) {
    parts.push(
      `Use the mcp__conveyor__create_pull_request tool to open PRs — it automatically stages, commits, and pushes changes before creating the PR. Do NOT use gh CLI or shell commands for PR creation.`,
    );
  }

  const modePrompt = buildModePrompt(agentMode, context, mode);
  if (modePrompt) {
    parts.push(modePrompt);
  }

  return parts.join("\n");
}
